<script lang="ts" setup>
import { computed, provide, ref, toRef, watch } from 'vue'
import { useScroll } from '@vueuse/core'
import { bookConfig as rocnovelConfig } from './configs/rocnovel'
import { bookConfig as ocereadConfig } from './configs/oceread'
import { bookConfig as talewaveConfig } from './configs/talewave'
import { bookConfig as ficmoonConfig } from './configs/ficmoon'
import { bookConfig as novelarcConfig } from './configs/novelarc'
import { bookConfig as storyellaConfig } from './configs/storyella'
import { bookConfig as silktaleConfig } from './configs/silktale'
import { bookConfig as pearlaConfig } from './configs/pearla'
import { bookConfig as fictoraConfig } from './configs/fictora'
import { bookConfig as marinovelConfig } from './configs/marinovel'
import { bookConfig as flipnovelConfig } from './configs/flipnovel'
import { bookConfig as moonpageConfig } from './configs/moonpage'
import { bookConfig as storlynConfig } from './configs/storlyn'
import { bookConfig as novarisConfig } from './configs/novaris'
import { useBookPage } from './composables/useBookPage'
import BookLoadingDefault from './components/BookLoading.default.vue'
import BookLoadingFlipnovel from './components/BookLoading.flipnovel.vue'
import BookPageDefault from './components/BookPage.default.vue'
import BookPageFicmoon from './components/BookPage.ficmoon.vue'
import BookPageNovelarc from './components/BookPage.novelarc.vue'
import BookPageMarinovel from './components/BookPage.marinovel.vue'
import BookPageFlipnovel from './components/BookPage.flipnovel.vue'
import BookPageStorlyn from './components/BookPage.storlyn.vue'
import BookPageTalewave from './components/BookPage.talewave.vue'
import { bannerShow, useBannerShow } from '@/composables/useBannerShow'
import { useBottomOverscroll } from '@/composables/useBottomOverscroll'
import { loadBrandComponent, loadBrandConfig } from '@/utils/loadBrandConfig'
import { useFbqViewContent } from '@/composables/fbq/useFbqViewContent'
import { t } from '@/i18n/utils'

const props = defineProps<{
  contentId: string
}>()

// 🔥 加载当前品牌配置（构建时 tree-shaking）
const config = loadBrandConfig({
  rocnovel: rocnovelConfig,
  oceread: ocereadConfig,
  talewave: talewaveConfig,
  ficmoon: ficmoonConfig,
  novelarc: novelarcConfig,
  storyella: storyellaConfig,
  silktale: silktaleConfig,
  pearla: pearlaConfig,
  fictora: fictoraConfig,
  marinovel: marinovelConfig,
  flipnovel: flipnovelConfig,
  moonpage: moonpageConfig,
  storlyn: storlynConfig,
  novaris: novarisConfig,
})

// 🔥 加载当前品牌组件（rocnovel 和 oceread 共用 default，ficmoon 用专属组件）
const BookPage = loadBrandComponent({
  rocnovel: BookPageDefault,
  oceread: BookPageDefault,
  talewave: BookPageTalewave,
  ficmoon: BookPageFicmoon,
  novelarc: BookPageNovelarc,
  storyella: BookPageNovelarc,
  silktale: BookPageNovelarc,
  pearla: BookPageNovelarc,
  fictora: BookPageFicmoon,
  marinovel: BookPageMarinovel,
  flipnovel: BookPageFlipnovel,
  moonpage: BookPageFlipnovel,
  storlyn: BookPageStorlyn,
  novaris: BookPageStorlyn,
})
const BookLoading = loadBrandComponent({
  rocnovel: BookLoadingDefault,
  oceread: BookLoadingDefault,
  talewave: BookLoadingDefault,
  ficmoon: BookLoadingDefault,
  novelarc: BookLoadingDefault,
  storyella: BookLoadingDefault,
  silktale: BookLoadingDefault,
  pearla: BookLoadingDefault,
  fictora: BookLoadingDefault,
  marinovel: BookLoadingDefault,
  flipnovel: BookLoadingFlipnovel,
  moonpage: BookLoadingFlipnovel,
  storlyn: BookLoadingFlipnovel,
  novaris: BookLoadingFlipnovel,
})

// 🔥 使用 composable 管理页面逻辑
const {
  book,
  coverUrl,
  introduce,
  paragraphs,
  skeleton,
  popupTOC,
  isInShelf,
  shelfLoading,
  onRead,
  onPullUpToNext,
  onToggleShelf,
  openPopupTOC,
  gotoTags,
  gotoBack,
} = useBookPage(toRef(props, 'contentId'), config)

// 容器引用（用于滚动等）
const containerRef = ref<HTMLElement>()
provide('containerRef', containerRef)

// 初始化 banner 显示逻辑
useBannerShow()

const { reportViewContent } = useFbqViewContent()

const stop = watch(() => book.value, (val) => {
  if (val) {
    reportViewContent(val.id, val.contentName, 'book')
    stop()
  }
}, { immediate: true })

// 底部上滑跳章节：在已触底的前提下，继续上滑累计超过阈值才跳转（顺滑、可靠）
// 触底加 6px 容差，规避 rem/缩放导致的亚像素误差，避免「滑到底却判定未触底」
const { arrivedState } = useScroll(containerRef, { offset: { bottom: 6 } })

let jumping = false
function handlePullUp() {
  if (jumping)
    return
  jumping = true
  onPullUpToNext()
  // 跳转失败（如无章节）时自愈，允许再次尝试
  setTimeout(() => {
    jumping = false
  }, 1000)
}

const { startListen: startOverscrollListen, stopListen: stopOverscrollListen } = useBottomOverscroll({
  containerRef,
  isAtBottom: computed(() => arrivedState.bottom),
  enabled: computed(() => !!book.value?.firstChapter),
  // 需在触底后继续上滑累计较大距离才触发，避免轻微滑动误跳
  threshold: 150,
  onTrigger: handlePullUp,
})

// 容器在 book 加载完成后才渲染，待其挂载/重建后再绑定监听
watch(containerRef, (el) => {
  stopOverscrollListen()
  if (el)
    startOverscrollListen()
})
</script>

<template>
  <div>
    <!-- 预加载图片 -->
    <div style="display: none;">
      <img :src="coverUrl" alt="Preload">
    </div>

    <!-- Loading 状态 -->
    <div v-if="skeleton">
      <component :is="BookLoading" :loading-text="config.texts.loading" />
    </div>

    <!-- 书籍不存在 -->
    <div v-else-if="!book">
      <BizBlockEmpty :title="config.texts.booksNotExist" />
    </div>

    <!-- 主内容 -->
    <div v-else ref="containerRef" class="h-100dvh pb-20 bg-n-bg-global overflow-scroll">
      <!-- 下载 Banner -->
      <BizDownloadApp
        v-if="bannerShow"
        :id="contentId"
        position="fixed"
        class="fixed top-0 w-full z-9999"
        from="book"
      />

      <!-- 🔥 品牌专属页面组件（封面 + 内容） -->
      <BookPage
        :popup-t-o-c="popupTOC"
        :book="book"
        :config="config"
        :cover-url="coverUrl"
        :introduce="introduce"
        :paragraphs="paragraphs"
        :banner-show="bannerShow"
        :is-in-shelf="isInShelf"
        :shelf-loading="shelfLoading"
        @update:popup-t-o-c="popupTOC = $event"
        @goto-tags="gotoTags"
        @open-toc="openPopupTOC"
        @read="onRead"
        @back="gotoBack"
        @toggle-shelf="onToggleShelf"
      />

      <!-- 底部上滑跳章节引导（点击也可触发，兜底下拉失败） -->
      <div
        class="pt-10px pb-20px flex flex-col items-center text-13px text-n-text-3 cursor-pointer transition-opacity"
        :class="arrivedState.bottom ? 'opacity-100' : 'opacity-60'"
        @click="handlePullUp"
      >
        <i-icon-park-outline-up class="text-18px animate-bounce" />
        <span class="mt-4px">{{ t('SwipeUpToContinue') }}</span>
      </div>
    </div>
  </div>
</template>
